import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { DirectLineActivity, DirectLineActivityType, DirectLineRole, OAuthCardContent, SuggestedAction } from './models/directline.models';

@Component({
    selector: 'app-scout-ai-message-view',
    standalone: false,
    templateUrl: './scout-ai-message-view.component.html',
    styleUrls: ['./scout-ai-message-view.component.css']
})
export class ScoutAiMessageViewComponent implements AfterViewChecked {
    @ViewChild('messageList') messageList!: ElementRef;
    @Input() set activities(value: DirectLineActivity[]) {
        this._activities = value;
        this.updateRelativeTimestamps();
    }
    get activities(): DirectLineActivity[] { return this._activities; }
    @Input() suggestedActions: SuggestedAction[] = [];
    @Input() isWaitingForResponse: boolean = false;
    @Input() userId: string = '';
    @Input() sessionEnded: boolean = false;
    @Output() sendMessage = new EventEmitter<string>();
    @Output() suggestedActionClick = new EventEmitter<SuggestedAction>();
    @Output() newConversation = new EventEmitter<void>();

    messageText: string = '';
    relativeTimeMap = new Map<string, string>();
    private _activities: DirectLineActivity[] = [];
    private shouldScrollToBottom: boolean = true;
    private lastActivityCount: number = 0;

    get visibleActivities(): DirectLineActivity[] {
        return this.activities.filter(a => a.type === DirectLineActivityType.MESSAGE && (a.text || this.hasOAuthCard(a)));
    }

    hasOAuthCard(activity: DirectLineActivity): boolean {
        return activity.attachments?.some(att =>
            att.contentType === 'application/vnd.microsoft.card.oauth' ||
            att.contentType === 'application/vnd.microsoft.card.signin'
        ) === true;
    }

    getSignInUrl(activity: DirectLineActivity): string {
        const card = activity.attachments?.find(att =>
            att.contentType === 'application/vnd.microsoft.card.oauth' ||
            att.contentType === 'application/vnd.microsoft.card.signin'
        );
        const cardContent = card ? (card.content as OAuthCardContent) : undefined;
        return cardContent?.buttons?.[0]?.value || cardContent?.tokenExchangeResource?.uri || '';
    }

    ngAfterViewChecked(): void {
        if (this.activities.length !== this.lastActivityCount) {
            this.lastActivityCount = this.activities.length;
            if (this.shouldScrollToBottom) {
                this.scrollToBottom();
            }
        }
    }

    onScroll(): void {
        const el = this.messageList?.nativeElement;
        if (!el) return;
        const threshold = 50;
        this.shouldScrollToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    }

    onSend(): void {
        const text = this.messageText.trim();
        if (!text) return;
        this.sendMessage.emit(text);
        this.messageText = '';
        this.shouldScrollToBottom = true;
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSend();
        }
    }

    onSuggestedActionClicked(action: SuggestedAction): void {
        this.suggestedActionClick.emit(action);
    }

    onStartNewConversation(): void {
        this.newConversation.emit();
    }

    isUserMessage(activity: DirectLineActivity): boolean {
        return activity.from?.id === this.userId || activity.from?.role === DirectLineRole.USER;
    }

    private updateRelativeTimestamps(): void {
        const now = Date.now();
        for (const activity of this.visibleActivities) {
            if (activity.id && activity.timestamp) {
                this.relativeTimeMap.set(activity.id, this.formatRelativeTime(activity.timestamp, now));
            }
        }
    }

    private formatRelativeTime(timestamp: string, now: number): string {
        const seconds = Math.floor((now - new Date(timestamp).getTime()) / 1000);
        if (seconds < 10) return 'Now';
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes} min ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    }

    trackByActivityId(_index: number, activity: DirectLineActivity): string {
        return activity.id;
    }

    trackByActionTitle(_index: number, action: SuggestedAction): string {
        return action.title;
    }

    private scrollToBottom(): void {
        try {
            const el = this.messageList?.nativeElement;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        } catch {
            // ignore
        }
    }
}
